import React, { useCallback, useState, useMemo, useEffect } from 'react'
import {
  Subheading,
  H3,
  Overline,
  InfoSheetPanel,
  Row,
  Button,
  BillboardButton,
  Card,
  InlineButton,
  List,
  ListItem,
  Separator,
  useOverlay,
  usePageWidth,
} from '@fs/zion-ui'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import { useTranslation, Trans } from 'react-i18next'
import { ContentAdd, DocumentRecordPerson } from '@fs/zion-icon'
import cloneDeep from 'lodash.clonedeep'
import { useFeatureFlags } from '@fs/zion-flags'
import zionDebug from '@fs/zion-debug'
import {
  getMostRecentFieldValue,
  getNestedElementById,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getNextUuid,
  getPersonName,
  getRelationshipToPrimaryPerson,
  getSexEnumFromResource,
  getSexTranslatedOrOrig,
  sexTypes,
  sortPersonNameSubElements,
  useGenderTranslations,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import HighlightButton from '../highlightButton/HighlightButton'
import { getGenderedRelTypeSuggestions, getPrimaryOrFirstPerson, sortFunction } from '../../utils/utils'
import { showAllNamesAndEventsHighlights } from '../../utils/highlightUtils'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import useTreatedNameFieldChips, {
  disableSaveFuncRequireOneField,
} from '../../../indexing/hooks/useTreatedNameFieldChips'
import { removeFieldFromValues } from '../../../indexing/components/editPanels/utils/editPanelFieldHelpers'
import { editPerson } from '../../utils/savePersonFunctions'
import { stepsMap } from '../../providers/FamilyReviewReducer'
import ConfirmRemoveOverlay from '../confirmRemoveOverlay/ConfirmRemoveOverlay'
import { deletePerson, removePersonFromRecord } from '../../utils/saveFunctions'
import ShareableField from '../shareableField/ShareableField'
import FormStandardizedSexField from '../../../../components/standardizedSexField/StandardizedSexField'
import { filterIllegalReviewFieldCharacters } from '../../../../../shared/utils/utils'

const debug = zionDebug('rip:familyReview:EditPerson')

const EditPersonForm = ({
  currentTask,
  editPersonOverlay,
  editHighlightsOverlay,
  stepTwoOverlay,
  stepOnePartTwoOverlay,
  onSubmit,
  primaryPerson,
  setSaveUpdateCalled,
}) => {
  debug({ currentTask, primaryPerson })
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const dispatch = useFamilyReviewDispatchContext()
  const state = useFamilyReviewStateContext()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const fieldSelectorOverlay = useOverlay()
  const confirmRemoveOverlay = useOverlay()
  const [selectedFields, setSelectedFields] = useState([])
  const [selectedFieldsWatch, setSelectedFieldsWatch] = useState([])
  const [selectedTempChips, setSelectedTempChips] = useState([])
  const [lockedTempChips, setLockedTempChips] = useState([])
  const [unlinkingStates, setUnlinkingStates] = useState({})
  const genderTranslations = useGenderTranslations()

  const person = currentTask?.subElements.find((el) => el?.id === state.selectedPersonId)
  const primaryPersonName = getPersonName({ person: primaryPerson, justOneName: true })

  const existingSexField = useMemo(() => getNestedSubelementsByType(person, 'SEX_CODE')?.[0], [person])
  const sexField = useMemo(() => existingSexField || { id: getNextUuid() }, [existingSexField])
  const [addedRelationshipField] = useState({ id: getNextUuid() })

  const nameElement =
    person?.subElements.find((el) => el.elementType === 'NAME' && el.nameType === 'BIRTH_NAME') ||
    person?.subElements.find((el) => el.elementType === 'NAME')
  const relationshipToPrimary =
    getRelationshipToPrimaryPerson({ person, primaryPersonId: primaryPerson?.id }) || addedRelationshipField

  // Only resolve a gender enum when the person actually has a SEX_CODE field: zion-sls now
  // always returns 'unknown' (never null) for a field with no resource, which would otherwise
  // make "no sex info entered" indistinguishable from "sex entered but unresolved" (FSS-14997)
  const sexENUM = existingSexField ? getSexEnumFromResource(sexField) : null
  const fieldOrder = ['NAME_GN', 'NAME_SURN', 'NAME_PRE', 'NAME_SPRE', 'NAME_SUF', 'NAME_PATR', 'NAME_MATR']

  const { selectedFieldData, treatedNameFieldChipData } = useTreatedNameFieldChips(
    nameElement,
    zionSlsState.types,
    selectedFields,
    selectedTempChips,
    lockedTempChips,
    true
  )

  if (selectedFieldData !== selectedFieldsWatch) {
    setSelectedFields(cloneDeep(selectedFieldData))
    setSelectedFieldsWatch(selectedFieldData)
  }

  const defaultValues = {}
  selectedFieldData.forEach((field) => {
    defaultValues[field?.id] = getMostRecentFieldValue(field) || ''
  })
  if (sexField) {
    defaultValues[sexField?.id] = {
      primaryText: existingSexField ? getSexTranslatedOrOrig(sexField, genderTranslations) || '' : '',
      key: sexENUM || '',
    }
    defaultValues[`${sexField?.id}_fieldType`] = 'SEX_CODE'
  }
  const order = relationshipToPrimary?.superElements?.find((superElement) => superElement?.id === person?.id)?.order
  const initialRelationshipOptions = getGenderedRelTypeSuggestions(zionSlsState.types, sexENUM) || []

  const selectedOption = relationshipToPrimary
    ? initialRelationshipOptions.find((option) => option.key === `${relationshipToPrimary.relType}_${order}`) ||
      initialRelationshipOptions.find((option) => option.key === `${relationshipToPrimary.relType}_FIRST`) // When the name for second and first is the same, only first shows up in the options
    : null
  if (relationshipToPrimary) {
    defaultValues[relationshipToPrimary?.id] = selectedOption
    defaultValues[`${relationshipToPrimary?.id}_fieldType`] = 'REL_TYPE'
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues,
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const valuesArray = Object.entries(values)
      const mappedFields = []
      valuesArray.forEach((value) => {
        const field = getNestedElementById(currentTask.subElements, value[0]) || {
          fieldType: values[`${value[0]}_fieldType`],
        }
        if (!mappedFields.find((mappedField) => mappedField?.id === value[0].replace('_fieldType', '')))
          mappedFields.push({ id: value[0], fieldType: field?.fieldType || field?.elementType, value: value[1] })
      })
      const givenName = mappedFields.find((field) => field.fieldType === 'NAME_GN')
      const surname = mappedFields.find((field) => field.fieldType === 'NAME_SURN')

      if (!givenName?.value && !surname?.value) {
        errors[givenName?.id] = {
          type: 'required',
          message: t('add-person.form-error-one', 'Must have at least one name part'),
        }
        errors[surname?.id] = {
          type: 'required',
          message: t('add-person.form-error-one', 'Must have at least one name part'),
        }
      }

      const mappedSexField = mappedFields.find((field) => field.fieldType === 'SEX_CODE')
      if (mappedSexField && (!mappedSexField?.value?.key || !sexTypes.includes(mappedSexField?.value?.key))) {
        errors[mappedSexField?.id] = { message: t('standardizedSexField.validation.warning', 'No Standard Selected') }
      }

      // For each name part field, check if it's field value contains illegal characters, and set an error if it does
      const namePartFields = ['NAME_GN', 'NAME_SURN', 'NAME_PRE', 'NAME_SPRE', 'NAME_SUF', 'NAME_PATR', 'NAME_MATR']
      namePartFields.forEach((namePart) => {
        const nameField = mappedFields.find((field) => field.fieldType === namePart)
        if (nameField) {
          const hasIllegalCharacters = nameField.value !== filterIllegalReviewFieldCharacters(nameField.value)
          if (hasIllegalCharacters) {
            errors[nameField?.id] = {
              type: 'invalid',
              message: t(
                'task-viewer.error.illegal-characters',
                "You can only enter ' - , . diacritic, and alphanumeric characters."
              ),
            }
          }
        }
      })

      return { values, errors }
    },
  })
  const dirtyFields = methods.formState.dirtyFields // Copying this into local state keeps the value current on submit. Otherwise, dirtyFields is empty.
  const formSexValue = useWatch({ name: sexField?.id, control: methods.control })
  const formRelationshipValue = useWatch({ name: relationshipToPrimary?.id, control: methods.control })
  const otherRelationshipType = useMemo(() => {
    return {
      key: 'OTHER',
      primaryText: 'No Direct Relation',
    }
  }, [])
  const relationshipOptions = useMemo(() => {
    const options = getGenderedRelTypeSuggestions(zionSlsState.types, formSexValue?.key) || []
    options.push(otherRelationshipType)
    return options
  }, [zionSlsState.types, formSexValue, otherRelationshipType])

  const setUnlinkingState = useCallback((fieldType, value) => {
    setUnlinkingStates((prevStates) => ({
      ...prevStates,
      [fieldType]: value,
    }))
  }, [])

  const getUnlinkingState = useCallback(
    (fieldType) => {
      return unlinkingStates[fieldType] || false
    },
    [unlinkingStates]
  )

  useEffect(() => {
    if (relationshipOptions && relationshipOptions.length) {
      const updatedOption = relationshipOptions.find((option) => option.key === formRelationshipValue?.key)
      if (updatedOption?.primaryText !== formRelationshipValue?.primaryText) {
        methods.setValue(relationshipToPrimary?.id, updatedOption)
      }
    }
  }, [relationshipOptions, methods, relationshipToPrimary?.id, formRelationshipValue])

  const handleChipSelection = (chips, lockedChips) => {
    const outFieldsThatStayed = (field) =>
      !chips.map(({ id }) => id).includes(field?.id) && !lockedChips.map(({ id }) => id).includes(field?.id)
    const newFields = [...selectedFields]
    const removeField = (field) => {
      removeFieldFromValues(field, methods)
      const index = newFields.findIndex((f) => f?.id === field?.id)
      newFields.splice(index, 1)
    }
    selectedFields.filter(outFieldsThatStayed).forEach(removeField)

    const newChips = chips.filter((chip) => !chip?.id)
    if (newChips) {
      newChips.forEach((chip) => {
        const newField = {
          label: chip.label,
          fieldType: chip.value.split(':')[0],
          id: getNextUuid(),
          added: true,
        }
        newFields.push(newField)
        methods.setValue(newField?.id, '')
        methods.setValue(`${newField?.id}_fieldType`, chip.value.split(':')[0])
      })
    }
    // create an object in the shape of what sortPersonNameSubElements can sort
    const pseudoPerson = {
      subElements: [{ elementType: 'NAME', subElements: newFields }],
    }
    sortPersonNameSubElements(pseudoPerson)
    setSelectedFields(newFields)
  }

  const hasSelectedHighlights = useCallback(
    (fieldId) => {
      return [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights].find(
        (highlight) => highlight.fieldId === fieldId
      )
    },
    [h]
  )

  const handleHighlightButtonClicked = (id) => {
    if (id) dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: id })
    const field = getNestedOrFloatingElementById([person], id)
    h.setActiveFieldInfo({ id, fieldType: field?.fieldType })
    h.setDisplayHighlights({
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      setMainZoom: true,
      setSplitScreenZoom: Boolean(state.splitScreenPartitionKey),
      attachFilter: (hl) => hl.fieldId === id,
      mainZoomFilter: (hl) => hl.fieldId === id,
      splitScreenZoomFilter: (hl) => hl.fieldId === id,
      isSmallScreen,
    })
  }

  const handleNotAPerson = async () => {
    await deletePerson({
      partitionKey: currentTask.partitionKey,
      personId: person?.id,
      zionSLSState: zionSlsState,
      zionSlsDispatch,
      userId: state.userId,
      saveChangesFlag,
    })
    setSaveUpdateCalled(true)
    confirmRemoveOverlay.close()
    editPersonOverlay.close()
  }

  const removePerson = async () => {
    try {
      await removePersonFromRecord({
        partitionKey: zionSlsState.currentImageArk,
        recordId: currentTask?.id,
        personId: person?.id,
        zionSLSState: zionSlsState,
        zionSlsDispatch,
        userId: state.userId,
        saveChangesFlag,
      })
      setSaveUpdateCalled(true)
      confirmRemoveOverlay.close()
      editPersonOverlay.close()
    } catch (err) {
      console.error(err)
    }
  }

  const setRelationshipAsOther = () => {
    methods.setValue(relationshipToPrimary?.id, otherRelationshipType)
    confirmRemoveOverlay.close()
  }

  return (
    <>
      <ZionForm
        methods={methods}
        onSubmit={(values) => {
          onSubmit(values, dirtyFields, nameElement?.id, unlinkingStates)
        }}
        id="edit-person"
      >
        {state.selectedPersonId === primaryPerson?.id && (
          <Card>
            <List>
              <ListItem
                startElement={<ListItem.Icon Icon={DocumentRecordPerson} />}
                primaryText={
                  <span>
                    <Trans i18nKey="family-review.editPerson.change-primary">
                      <span>This is the </span>
                      <strong>Primary Person.</strong>
                      <span> To change </span>
                      <InlineButton
                        linkName="Edit Person Go Back To Step 1"
                        onClick={() => {
                          editPersonOverlay.close()
                          dispatch({
                            type: 'SET_CURRENT_STEP',
                            value: stepsMap.stepOne,
                          })
                          stepTwoOverlay.close()
                          stepOnePartTwoOverlay.handleClick()
                        }}
                      >
                        Go back to step 1
                      </InlineButton>
                      .
                    </Trans>
                  </span>
                }
              />
            </List>
          </Card>
        )}
        <Separator size="xs" />
        {[...selectedFields]
          .sort((a, b) => fieldOrder.indexOf(a.fieldType) - fieldOrder.indexOf(b.fieldType))
          .map((field) => {
            return (
              <div key={field?.id}>
                <ShareableField
                  field={field}
                  unlinkingState={[
                    getUnlinkingState(field.fieldType),
                    (value) => setUnlinkingState(field.fieldType, value),
                  ]}
                  editHighlightsOverlay={editHighlightsOverlay}
                  handleHighlightButtonClicked={handleHighlightButtonClicked}
                  hasSelectedHighlights={hasSelectedHighlights}
                  fieldType="person"
                />
              </div>
            )
          })}
        {sexField?.id && (
          <>
            <FormStandardizedSexField
              person={person}
              fieldName={sexField?.id}
              key={sexField?.id}
              addedSexCode={sexField}
            />
            <HighlightButton
              editHighlightsOverlay={editHighlightsOverlay}
              id={sexField?.id}
              handleHighlightButtonClicked={handleHighlightButtonClicked}
              isAdding={!hasSelectedHighlights(sexField?.id)}
            />
          </>
        )}
        {relationshipToPrimary && state?.selectedPersonId !== primaryPerson?.id && (
          <FormAutosuggest
            label={t('family-review.editPerson.relationship-to-person', 'Relationship To {primaryPersonName}', {
              primaryPersonName,
            })}
            placeholder={t('family-review.editPerson.relationship-to-person', 'Relationship To {primaryPersonName}', {
              primaryPersonName,
            })}
            name={relationshipToPrimary?.id}
            maxLength={256}
            suggestions={relationshipOptions}
            data-testid="edit-person-relationship-to-primary"
            sortFunction={sortFunction}
            highlightMatchingText
          />
        )}
        <Separator />
        <BillboardButton
          data-testid="add-or-remove-fields"
          Icon={ContentAdd}
          color="yellow"
          emphasis="low"
          onClick={fieldSelectorOverlay.handleClick}
          linkName="Edit Person Add or Remove Fields"
        >
          {t('edit-index.add-fields.button', '+ Add or Remove Fields')}
        </BillboardButton>
        <Separator />
        <BillboardButton
          data-testid="remove-person-button"
          color="red"
          emphasis="medium"
          onClick={confirmRemoveOverlay.handleClick}
          disabled={state.selectedPersonId === primaryPerson?.id}
          linkName="Edit Person Remove"
        >
          {t('edit-index.remove-person.button', 'Remove {name}', {
            name: selectedFieldData.find((field) => field.fieldType === 'NAME_GN')?.fieldValues?.[0]?.origValue?.text,
          })}
        </BillboardButton>
      </ZionForm>
      <FieldSelectorOverlay
        title={t('edit-index.add-fields.title', 'Add or Remove Fields')}
        chipData={[
          {
            title: t('edit-index.chips.name-fields.title', 'Name'),
            data: treatedNameFieldChipData,
            key: `nameFields-${nameElement?.id}`,
          },
        ]}
        fieldData={selectedFields}
        handleSelection={handleChipSelection}
        handleSelectionChange={(chips, lockedChips) => {
          setSelectedTempChips(chips)
          setLockedTempChips(lockedChips)
        }}
        overlay={fieldSelectorOverlay}
        disabledFunc={disableSaveFuncRequireOneField}
        unlinkingStates={unlinkingStates}
      />
      <ConfirmRemoveOverlay
        confirmRemoveOverlay={confirmRemoveOverlay}
        currentTask={currentTask}
        handleNotAPerson={handleNotAPerson}
        removePerson={removePerson}
        setRelationshipAsOther={setRelationshipAsOther}
      />
    </>
  )
}

export default function EditPerson({
  currentTask,
  editPersonOverlay,
  editHighlightsOverlay,
  stepTwoOverlay,
  stepOnePartTwoOverlay,
  reviewPersonEventsOverlay,
  setSaveUpdateCalled,
  setStepTwoData,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const state = useFamilyReviewStateContext()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const dispatch = useFamilyReviewDispatchContext()
  const primaryPerson = getPrimaryOrFirstPerson(currentTask)
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])

  const setStepFourHighlights = useCallback(
    (highlights) => {
      if (state.currentStep === STEPS.STEP_FOUR) {
        const allPersons = currentTask.subElements.filter((e) => e.elementType === 'PERSON')
        const personIds = allPersons.flatMap((person) => person?.id)
        showAllNamesAndEventsHighlights({
          h,
          currentTask,
          highlights,
          personIds,
          isSmallScreen,
          splitScreenPartitionKey: state.splitScreenPartitionKey,
        })
      }
    },
    [currentTask, state, isSmallScreen, h]
  )

  const handleCloseEditPerson = useCallback(
    (markThisCardCorrect = false, highlights = []) => {
      editPersonOverlay.close()
      if (state.currentStep === STEPS.STEP_TWO) {
        setStepTwoData(markThisCardCorrect ? state.selectedPersonId : null, highlights)
      }
      if (state.currentStep === STEPS.STEP_FOUR) {
        setStepFourHighlights(highlights)
      }
    },
    [editPersonOverlay, state, setStepTwoData, setStepFourHighlights]
  )

  const cancelButtonClicked = () => {
    const recentlyClearedMainHighlights = h.clearEdits().mainHighlights
    handleCloseEditPerson(false, recentlyClearedMainHighlights)
    if (state.currentStep === STEPS.STEP_FOUR) reviewPersonEventsOverlay.open()
  }

  const onSubmit = useCallback(
    async (values, dirtyFields, nameElementId, unlinkingStates) => {
      await editPerson({
        currentTask,
        partitionKey: zionSlsState.currentImageArk,
        personId: state.selectedPersonId,
        formValues: values,
        unlinkingNames: unlinkingStates,
        dirtyFields,
        userId: state.userId,
        nameElementId,
        highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
        relatedPersonId: primaryPerson?.id,
        zionSLSState: zionSlsState,
        zionSlsDispatch,
        saveChangesFlag,
        handleError: (error) => dispatch({ type: 'SET_ERROR', value: error }),
        handleSuccess: () => {
          setSaveUpdateCalled(true)
          handleCloseEditPerson(true, [
            ...h.getHighlights().mainEditHighlights,
            ...h.getHighlights().splitScreenEditHighlights,
          ])
          if (state.currentStep === STEPS.STEP_FOUR) reviewPersonEventsOverlay.open()
        },
      })
    },
    [
      h,
      state,
      currentTask,
      primaryPerson?.id,
      zionSlsState,
      zionSlsDispatch,
      saveChangesFlag,
      setSaveUpdateCalled,
      handleCloseEditPerson,
      reviewPersonEventsOverlay,
      dispatch,
    ]
  )

  const userClosedPanel = () => {
    const recentlyClearedMainHighlights = h.clearEdits().mainHighlights
    handleCloseEditPerson(false, recentlyClearedMainHighlights)
  }

  return (
    <InfoSheetPanel
      {...{ ...editPersonOverlay, close: userClosedPanel }}
      title={t('family-review.step3.heading', 'Family Relationships')}
      headingColor="green"
      footer={
        <Row alignX="end">
          <Button onClick={cancelButtonClicked} data-testid="edit-person-cancel-button" linkName="Edit Person Cancel">
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            linkName="Edit Person Save"
            form="edit-person"
            type="submit"
            data-testid="edit-person-save-button"
          >
            {t('edit-index.save.button', 'Save')}
          </Button>
        </Row>
      }
    >
      {editPersonOverlay.isOpen && (
        <>
          <H3 size="H5">
            <Overline>
              {t('family-review.stepXof4', 'Step {step} of 4', {
                step: state.currentStep === STEPS.STEP_TWO ? '2' : '4',
              })}
            </Overline>
            {t('family-review.editPerson.heading', 'Edit Name and Relationship')}
            <Subheading>
              {t(
                'family-review.editPerson.subHeading',
                'Fix any incorrect fields or highlights. Add or remove fields as necessary.'
              )}
            </Subheading>
          </H3>
          <Separator />
          {editPersonOverlay.isOpen && (
            <EditPersonForm
              currentTask={currentTask}
              editPersonOverlay={editPersonOverlay}
              editHighlightsOverlay={editHighlightsOverlay}
              stepTwoOverlay={stepTwoOverlay}
              stepOnePartTwoOverlay={stepOnePartTwoOverlay}
              onSubmit={onSubmit}
              primaryPerson={primaryPerson}
              setSaveUpdateCalled={setSaveUpdateCalled}
            />
          )}
        </>
      )}
    </InfoSheetPanel>
  )
}
